/**
 * Reading the few things a host needs to know about a saved scene document
 * WITHOUT applying it. Two formats reach here: the older `of-vjson`, and the
 * v65 whole-scene `of-scene/2` (a Vault save is that document as JSON, its
 * files referenced by URL or, under a size budget, inlined).
 */
import { OF_SCENE_SCHEMA } from "../shared/api-contract";

interface SceneAssetRef {
  id?: unknown;
  name?: unknown;
  bufferView?: unknown;
  dataUrl?: unknown;
  url?: unknown;
  session?: unknown;
}

interface SavedSceneDoc {
  schema?: unknown;
  /** of-vjson */
  sceneState?: {
    backgroundSource?: string;
    backgroundImageUrl?: string | null;
  };
  /** of-scene/2 */
  sections?: Record<string, unknown>;
  assets?: unknown;
  warnings?: unknown;
}

export interface SavedSceneReport {
  /** The backdrop was a picture that could not be kept (see below). */
  lostBackdrop: boolean;
  /** of-scene/2: the files the document names but holds no bytes or address for. */
  lostFiles: string[];
  /** of-scene/2: what the viewer itself noted while saving. */
  warnings: string[];
}

const NOTHING: SavedSceneReport = { lostBackdrop: false, lostFiles: [], warnings: [] };

/** An asset with no way to get its bytes back: no packed bytes, no inline copy, no address. */
const isLost = (a: SceneAssetRef) =>
  typeof a.bufferView !== "number" && !a.dataUrl && !a.url && !a.session;

/** Every string anywhere inside `v` — how a section names the assets it uses, whatever the field is called. */
function strings(v: unknown, out: Set<string>, depth = 0): Set<string> {
  if (depth > 8) return out;
  if (typeof v === "string") out.add(v);
  else if (Array.isArray(v)) for (const x of v) strings(x, out, depth + 1);
  else if (v && typeof v === "object") for (const x of Object.values(v)) strings(x, out, depth + 1);
  return out;
}

function reportV2(doc: SavedSceneDoc): SavedSceneReport {
  const assets = (Array.isArray(doc.assets) ? doc.assets : []).filter(
    (a): a is SceneAssetRef => !!a && typeof a === "object",
  );
  const lost = assets.filter(isLost);
  const lostIds = new Set(lost.map((a) => a.id).filter((id): id is string => typeof id === "string"));
  const inBackground = strings(doc.sections?.background, new Set());
  return {
    lostBackdrop: [...lostIds].some((id) => inBackground.has(id)),
    lostFiles: lost.map((a) => (typeof a.name === "string" && a.name ? a.name : "a file")),
    warnings: (Array.isArray(doc.warnings) ? doc.warnings : []).filter((w): w is string => typeof w === "string" && !!w.trim()),
  };
}

/**
 * What a saved scene could not keep. Cheap: one JSON.parse of a document the
 * caller is about to hand to the viewer (or has just been handed) anyway.
 */
export function savedSceneReport(json: string): SavedSceneReport {
  let doc: SavedSceneDoc | null;
  try {
    doc = JSON.parse(json) as SavedSceneDoc | null;
  } catch {
    return NOTHING; // not our document shape — the viewer will report its own failure
  }
  if (!doc || typeof doc !== "object") return NOTHING;
  if (doc.schema === OF_SCENE_SCHEMA) return reportV2(doc);
  const s = doc.sceneState;
  return { ...NOTHING, lostBackdrop: !!s && s.backgroundSource === "image" && !s.backgroundImageUrl };
}

/**
 * True when this document was saved with an image backdrop that cannot be
 * restored — an image the user picked from disk that was too large for the
 * viewer to keep in the document (of-vjson: `SceneState.backgroundImageUrl`
 * left empty; of-scene/2: the backdrop's asset has neither bytes nor an
 * address).
 *
 * The viewer deliberately keeps the live background in that case rather than
 * blanking the scene to transparent, so nothing on screen says the backdrop is
 * missing; this is what lets the host say it out loud.
 */
export function savedSceneLostBackdrop(json: string): boolean {
  return savedSceneReport(json).lostBackdrop;
}

/**
 * One plain sentence about the files a save could not keep, or null when it
 * kept everything. For the toast after a Vault save.
 */
export function lostFilesSentence(report: SavedSceneReport, budgetMb: number): string | null {
  const names = [...new Set(report.lostFiles)];
  if (names.length > 0) {
    const shown = names.slice(0, 3).join(", ") + (names.length > 3 ? ` and ${names.length - 3} more` : "");
    return `${shown} ${names.length === 1 ? "is a file" : "are files"} from your computer over ${budgetMb} MB, so ${names.length === 1 ? "it was" : "they were"} not saved with the scene. Everything else was.`;
  }
  if (report.warnings.length > 0) return report.warnings.slice(0, 2).join(" ");
  return null;
}
